import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge } from '@/components/ui/daisy';
import LessonPlanItem, { LessonPlanItemDTO } from '@/models/LessonPlanItem';
import { Breakpoints } from '@/utils/Breakpoint';

import LessonPlanItemInlineForm from './LessonPlanItemInlineForm';

export type LessonPlanItemCardProps = {
  lessonPlanItem: LessonPlanItem;
  index: number;
  coursePathId: number;
  courseLineId: number;
  onDelete: (lessonPlanItemId: number) => Promise<void>;
  onUpdate?: (params: {
    lessonPlanItemId: number;
    data: Partial<LessonPlanItemDTO>;
  }) => Promise<LessonPlanItem>;
  isUpdating?: boolean;
  isDeleting?: boolean;
  isDraggable?: boolean;
  isReordering?: boolean;
};
export default function LessonPlanItemCard({
  lessonPlanItem,
  index,
  coursePathId,
  courseLineId,
  onDelete,
  onUpdate,
  isUpdating,
  isDeleting,
  isDraggable,
  isReordering,
}: LessonPlanItemCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonPlansTab',
  });

  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  const [isEditing, setIsEditing] = useState(false);

  const dragId = `lessonPlanItem-${lessonPlanItem.id}`;

  if (isEditing) {
    return (
      <CourseAdminCard.DraggableInlineForm
        id={dragId}
        index={index}
        isDraggable={isDraggable}
      >
        <LessonPlanItemInlineForm
          coursePathId={coursePathId}
          courseLineId={courseLineId}
          defaultItem={lessonPlanItem}
          onSubmit={async data => {
            await onUpdate?.({ lessonPlanItemId: lessonPlanItem.id, data });
            setIsEditing(false);
          }}
          onCancel={() => setIsEditing(false)}
          isLoading={isUpdating}
        />
      </CourseAdminCard.DraggableInlineForm>
    );
  }

  const dragProps = isDraggable
    ? {
        isDraggable: true as const,
        id: dragId,
        index,
        disabled: isReordering,
      }
    : { isDraggable: false as const };

  return (
    <CourseAdminCard
      variant="card"
      isDeleting={isDeleting}
      className="mb-6"
      {...dragProps}
    >
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading>
          <CourseAdminCard.Order value={lessonPlanItem.order} />
          <CourseAdminCard.Title className="font-normal ">
            {lessonPlanItem.name}
          </CourseAdminCard.Title>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          <CourseAdminCard.Badges>
            <ConditionalRenderer condition={lessonPlanItem.isEvaluation}>
              <Badge color="info" size={isXl ? 'md' : 'sm'}>
                {t('evaluationBadge')}
              </Badge>
            </ConditionalRenderer>
            <Badge color="success" size={isXl ? 'md' : 'sm'}>
              {t('minutes', { time: lessonPlanItem.time })}
            </Badge>
          </CourseAdminCard.Badges>
          {onUpdate && (
            <CourseAdminCard.EditButton onClick={() => setIsEditing(true)} />
          )}
          <CourseAdminCard.MoreActions>
            <CourseAdminCard.MoreActions.Delete
              confirm
              onClick={() => onDelete(lessonPlanItem.id)}
            />
          </CourseAdminCard.MoreActions>
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
